import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownCheckboxItem
} from '@lsq/nextgen/v2/dropdown';
import Search from '@lsq/nextgen/v2/text-field/search-bar';
import styles from './Content.module.css';

export const MODE = { SINGLE: 'single', MULTI: 'multi' };

/**
 * Dropdown body for FilterDropdown. Optionally renders a search input
 * (only when `onSearchChange` is defined), then a list of options filtered
 * in-memory by the current search term. Uses radio items for single-select,
 * checkbox items for multi.
 */
const Content = ({
  options = [],
  mode = MODE.SINGLE,
  selectedOptions = [],
  onChange,
  searchValue,
  onSearchChange,
  className = ''
}) => {
  const selectedValues = useMemo(
    () => new Set(selectedOptions.map((o) => o.value)),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    const term = (searchValue || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.label?.toLowerCase().includes(term));
  }, [options, searchValue]);

  const handleSingle = (value) => {
    const option = options.find((o) => o.value === value);
    onChange?.(option ? [option] : []);
  };

  const handleMulti = (value, checked) => {
    const option = options.find((o) => o.value === value);
    if (!option) return;
    const next = checked
      ? [...selectedOptions, option]
      : selectedOptions.filter((o) => o.value !== value);
    onChange?.(next);
  };

  const isSearchable = typeof onSearchChange === 'function';

  return (
    <DropdownContent className={className} align="start" sideOffset={4}>
      {isSearchable ? (
        <div className={styles.searchWrapper} onKeyDown={(e) => e.stopPropagation()}>
          <Search
            value={searchValue ?? ''}
            onChange={(val) => onSearchChange(val)}
            placeholder="Search"
            size="xs"
            data-testid="filter-dropdown-search"
          />
        </div>
      ) : null}

      {filteredOptions.length === 0 ? (
        <div className={styles.empty}>No options</div>
      ) : mode === MODE.MULTI ? (
        filteredOptions.map((opt) => (
          <DropdownCheckboxItem
            key={opt.value}
            className={styles.item}
            checked={selectedValues.has(opt.value)}
            onCheckedChange={(checked) => handleMulti(opt.value, checked)}
            onSelect={(e) => e.preventDefault()}>
            {opt.label}
          </DropdownCheckboxItem>
        ))
      ) : (
        <DropdownRadioGroup value={selectedOptions[0]?.value ?? ''} onValueChange={handleSingle}>
          {filteredOptions.map((opt) => (
            <DropdownRadioItem key={opt.value} value={opt.value} className={styles.item}>
              {opt.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      )}
    </DropdownContent>
  );
};

Content.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  mode: PropTypes.oneOf(Object.values(MODE)),
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ),
  onChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  className: PropTypes.string
};

export default Content;
